import type { TaskList } from '~/types/taskList.type'
import { TaskListCompleteModal, TaskListDeleteModal, TaskListModal, TaskListRestoreModal } from '#components'
import { unknownError } from '~/constants'

export default function () {
  const overlay = useOverlay()
  const toast = useToast()
  const loadingBar = useNaiveLoadingBar()

  type TaskListModalProps = InstanceType<typeof TaskListModal>['$props']

  const taskListModal = overlay.create(TaskListModal)

  async function openTaskListCreateModal(options: Pick<TaskListModalProps, 'project' | 'name'>, onTaskListCreated?: (taskList: TaskList<'withTasks'>) => any) {
    await taskListModal.open({
      project: options.project,
      name: options.name,
      onTaskListCreated,
    })
  }

  const taskListToEditId = ref<TaskList['id']>(0)

  const {
    data: taskListToEditData,
    error: taskListToEditError,
    status: taskListToEditStatus,
    execute: fetchTaskListToEdit,
  } = useApiTaskListGetById(taskListToEditId, {
    withProject: true,
    withTasks: true,
    withNotificationThresholds: true,
  }, {
    immediate: false,
    watch: false,
  })

  const isAnyTaskListEditModalLoading = computed(() => taskListToEditStatus.value === 'pending')

  function isTaskListEditModalLoading(taskListId: TaskList['id']) {
    return computed(() => isAnyTaskListEditModalLoading.value && taskListToEditId.value === taskListId)
  }

  async function openTaskListEditModal(
    taskList: TaskList<undefined>,
    options: Pick<TaskListModalProps, 'project'>,
    onTaskListEdited?: (taskList: TaskList<'withTasks'>) => any,
    onTaskListDeleted?: () => any,
  ) {
    taskListToEditId.value = taskList.id
    await fetchTaskListToEdit()

    if (taskListToEditData.value && !taskListToEditError.value) {
      return taskListModal.open({
        title: 'Aufgabenliste bearbeiten',
        project: options.project,
        taskList: taskListToEditData.value?.data,
        onTaskListEdited,
        onTaskListDeleted,
      })
    }
    else if (taskListToEditError.value) {
      toast.add({ color: 'error', description: taskListToEditError.value.data?.message ?? unknownError })
    }
  }

  const taskListDeleteModal = overlay.create(TaskListDeleteModal)

  async function openTaskListDeleteDialog(taskList: TaskList<undefined>, onTaskListDeleted?: () => any) {
    return await taskListDeleteModal.open({
      taskList,
      onClose: async (isConfirmed) => {
        if (isConfirmed)
          await deleteTaskList(taskList, onTaskListDeleted)
      },
    })
  }

  const taskListToDeleteId = ref<TaskList<undefined>['id']>(0)
  const { error: taskListDeleteError, execute: executeTaskListDelete } = useApiTaskListDeleteById(taskListToDeleteId)

  async function deleteTaskList(taskList: TaskList<undefined>, onTaskListDeleted?: () => any) {
    taskListToDeleteId.value = taskList.id
    loadingBar.start()
    await executeTaskListDelete()
    loadingBar.finish()

    if (!taskListDeleteError.value) {
      toast.add({ color: 'success', description: 'Aufgabenliste erfolgreich gelöscht!' })

      if (onTaskListDeleted)
        onTaskListDeleted()
    }
    else {
      toast.add({ color: 'error', description: taskListDeleteError.value.data?.message ?? unknownError })
    }
  }

  const taskListUpdateCompletedParams = ref<Pick<TaskList<undefined>, 'isCompleted'>>({
    isCompleted: false,
  })

  const taskListIdToToggleCompleted = ref<TaskList<undefined>['id']>(0)

  const {
    data: taskListUpdateCompletedData,
    error: taskListUpdateCompletedError,
    execute: updateTaskListCompleted,
    status: taskListUpdateCompletedStatus,
  } = useApiTaskListUpdateById(taskListIdToToggleCompleted, taskListUpdateCompletedParams)

  const isAnyTaskListToggleCompletedLoading = computed(() => taskListUpdateCompletedStatus.value === 'pending')

  function isTaskListToggleCompletedLoading(taskListId: TaskList<undefined>['id']) {
    return computed(() => isAnyTaskListToggleCompletedLoading.value && taskListIdToToggleCompleted.value === taskListId)
  }

  function openTaskListToggleCompletedDialog(
    taskList: TaskList<undefined>,
    onToggleCompleted?: (isCompleted: boolean) => any,
  ) {
    if (taskList.isCompleted)
      openRestoreDialog(taskList, onToggleCompleted)
    else
      openArchiveDialog(taskList, onToggleCompleted)
  }

  const taskListRestoreModal = overlay.create(TaskListRestoreModal)

  async function openRestoreDialog(
    taskList: TaskList<undefined>,
    onToggleCompleted?: (isCompleted: boolean) => any,
  ) {
    taskListRestoreModal.open({
      taskList,
      onClose: async (isConfirmed) => {
        if (isConfirmed)
          await toggleTaskListCompleted(taskList, onToggleCompleted)
      },
    })
  }

  const taskListCompleteModal = overlay.create(TaskListCompleteModal)

  async function openArchiveDialog(
    taskList: TaskList<undefined>,
    onToggleCompleted?: (isCompleted: boolean) => any,
  ) {
    taskListCompleteModal.open({
      taskList,
      onClose: async (isCompleted: boolean) => {
        if (isCompleted)
          await toggleTaskListCompleted(taskList, onToggleCompleted)
      },
    })
  }

  async function toggleTaskListCompleted(
    taskList: TaskList<undefined>,
    onToggleCompleted?: (isCompleted: boolean) => any,
  ) {
    taskListIdToToggleCompleted.value = taskList.id
    taskListUpdateCompletedParams.value = {
      isCompleted: !taskList.isCompleted,
    }
    await updateTaskListCompleted()

    if (taskListUpdateCompletedData.value) {
      toast.add({ color: 'success', description: `Aufgabenliste erfolgreich ${taskList.isCompleted ? 'wieder geöffnet' : 'abgeschlossen'}!` })
      if (onToggleCompleted)
        onToggleCompleted(taskListUpdateCompletedData.value.data.isCompleted)
    }
    else if (taskListUpdateCompletedError.value) {
      toast.add({ color: 'error', description: taskListUpdateCompletedError.value.data?.message ?? unknownError })
    }
  }

  return {
    openTaskListCreateModal,
    isAnyTaskListEditModalLoading,
    isTaskListEditModalLoading,
    openTaskListEditModal,
    openTaskListDeleteDialog,
    openTaskListToggleCompletedDialog,
    isAnyTaskListToggleCompletedLoading,
    isTaskListToggleCompletedLoading,
  }
}
